<template>
  <WhiteIQOS
    bodyClass="price-group-panel-iqos"
    noOverflow
    :title="`${price_group_index + 1}. ${$t('modificators_settings.group')}`"
  >
    <template #actions>
      <div
        class="iqos-tooltip-helper-box"
        @mouseenter="isPosLockTooltipShown = true"
        @mouseleave="isPosLockTooltipShown = false"
        v-if="isPriceGroupFromPosSystem"
      >
        <BaseIcon
          icon="lock"
          size="sm2"
        />
        <IQOSTooltip
          :backdrop="false"
          earPosition="bottom-right"
          :title="$t('modificators_settings.locked_changes')"
          v-if="isPosLockTooltipShown"
        >
          {{ $t("modificators_settings.cant_delete_group") }}
        </IQOSTooltip>
      </div>
      <div
        class="iqos-tooltip-helper-box"
        @mouseenter="isDependencyTooltipShown = true"
        @mouseleave="isDependencyTooltipShown = false"
        v-if="price_group.show_for && price_group.show_for.length"
      >
        <BaseIcon
          icon="link"
          size="sm2"
        />
        <IQOSTooltip
          :backdrop="false"
          earPosition="bottom-right"
          :title="$t('modificators_settings.linked_tip_title')"
          v-if="isDependencyTooltipShown"
        >
          <div class="gap-row-1xs">
            <span>{{ $t("modificators_settings.linked_tip_desc") }}</span>
            <div class="gap-row-zero">
              <div
                v-for="dep in groupDependenciesList"
                :key="dep.item_id"
              >
                • {{ dep.name }} ({{ dep.group_name }})
              </div>
            </div>
          </div>
        </IQOSTooltip>
      </div>
    </template>
    <BaseDivider color="border-base-tertiary" />
    <div class="price-group-settings">
      <div class="gap-row-txs name-block">
        <div class="qrw-label-sm qrw-text-content-base-tertiary bold">
          {{ $t("modificators_settings.name_hint") }}
        </div>
        <BaseTextInput
          v-model="price_group.name"
          size="sm"
          @blur="save"
          v-if="!isLocalizing"
          :placeholder="$t('modificators_settings.placeholder_required')"
        />
        <BaseTextInput
          v-model="
            dish.i18n[currentProductLocaleIndex].price_groups[price_group_index]
              .name
          "
          size="sm"
          @blur="save"
          v-else
          :placeholder="
            price_group.name || $t('modificators_settings.placeholder_required')
          "
        />
      </div>
      <BaseDivider
        color="border-base-tertiary"
        vertical
        style="height: 60px;"
      />
      <div class="gap-row-txs type-block">
        <div class="qrw-label-sm qrw-text-content-base-tertiary bold">
          {{ $t("modificators_settings.type_hint") }}
        </div>
        <div class="gap-column-sm">
          <BaseBetaRadioButton
            :value="price_group.single_select"
            @input="onSingleSelectChange"
            :checkedValue="true"
            size="lg"
          >
            {{ $t("modificators_settings.variant_name") }}
          </BaseBetaRadioButton>
          <BaseBetaRadioButton
            :value="price_group.single_select"
            @input="onSingleSelectChange"
            :checkedValue="false"
            size="lg"
          >
            {{ $t("modificators_settings.modif_name") }}
          </BaseBetaRadioButton>
        </div>
      </div>
      <BaseDivider
        color="border-base-tertiary"
        vertical
        style="height: 60px;"
      />
      <div class="gap-row-txs price-block">
        <div class="qrw-label-sm qrw-text-content-base-tertiary bold">
          {{ $t("modificators_settings.selection_hint") }}
        </div>
        <div class="gap-column-xs">
          <BaseNumberInput
            class="limit-input"
            v-model.number="price_group.min_count"
            :disabled="isPriceGroupFromPosSystem"
            placeholder="0"
            @blur="save"
            unit="min"
            v-if="price_group.single_select"
          />
          <BaseNumberInput
            class="limit-input"
            :value="0"
            @blur="save"
            unit="min"
            disabled
            v-else
          />
          <BaseNumberInput
            class="limit-input"
            v-model.number="price_group.max_count"
            placeholder="0"
            :disabled="isPriceGroupFromPosSystem"
            @blur="save"
            unit="max"
          />
        </div>
      </div>
      <BaseDivider
        color="border-base-tertiary"
        vertical
        style="height: 60px;"
      />
      <div class="gap-row-txs">
        <div class="modificators-dependencies-title">
          <div class="qrw-label-sm qrw-text-content-base-tertiary bold">
            {{ $t("modificators_settings.dependencies_title") }}
          </div>

          <div
            class="iqos-tooltip-helper-box"
            @mouseenter="isShowingInfoDependenciesTip = true"
            @mouseleave="isShowingInfoDependenciesTip = false"
          >
            <BaseIcon
              class="qrw-text-content-base-tertiary"
              icon="info-circle"
              size="1xs"
            />
            <IQOSTooltip
              :backdrop="false"
              earPosition="bottom-right"
              :title="$t('modificators_settings.dependencies_title')"
              v-if="isShowingInfoDependenciesTip"
            >
              {{ $t("modificators_settings.dependencies_info_tip") }}
            </IQOSTooltip>
          </div>
        </div>

        <BasePushButton
          variant="outlined"
          size="sm"
          borderColor="border-base-secondary"
          textColor="content-base-primary"
          class="deps-button"
          @click="managingDependencies = true"
        >
          {{ $t("modificators_settings.dependencies_btn") }}
        </BasePushButton>
      </div>
    </div>
    <div class="modificators-list">
      <div
        class="modificator-card"
        v-for="(item, item_index) in price_group.items"
      >
        <div class="modificator-card-info">
          <div class="qrw-heading-xs bold qrw-text-content-base-primary">
            {{ price_group_index + 1 }}.{{ item_index + 1 }}
          </div>
          <div class="gap-column-1xs">
            <BaseIconButton
              variant="outlined"
              textColor="content-base-primary"
              borderColor="border-base-secondary"
              icon="chevron-down"
              size="sm"
              @click="moveGroupItem(item_index, 1)"
              v-if="item_index !== price_group.items.length - 1"
            />
            <BaseIconButton
              textColor="content-base-primary"
              borderColor="border-base-secondary"
              variant="outlined"
              @click="moveGroupItem(item_index, -1)"
              icon="chevron-up"
              size="sm"
              v-if="item_index != 0"
            />
          </div>
        </div>
        <BaseDivider
          color="border-base-tertiary"
          class="modificator-card-vertical-divider"
          vertical
          style="height: 100%;"
        />
        <div class="gap-row-sm w-100">
          <div class="modificator-card-general w-100">
            <div class="gap-column-sm w-100">
              <div class="gap-row-txs w-100">
                <div class="qrw-label-sm qrw-text-content-base-tertiary bold">
                  {{ $t("modificators_settings.item_placeholder") }}
                </div>
                <BaseTextInput
                  size="sm"
                  v-model="item.name"
                  @blur="save"
                  :placeholder="$t('modificators_settings.item_placeholder')"
                  v-if="!isLocalizing"
                />
                <BaseTextInput
                  size="sm"
                  v-model="
                    dish.i18n[currentProductLocaleIndex].price_groups[
                      price_group_index
                    ].items[item_index]
                  "
                  @blur="save"
                  :placeholder="
                    item.name || $t('modificators_settings.item_placeholder')
                  "
                  v-else
                />
              </div>
              <div class="gap-column-sm w-100">
                <div
                  class="gap-row-txs w-100"
                  v-if="isUsingServiceReservations"
                >
                  <div class="qrw-label-sm qrw-text-content-base-tertiary bold">
                    {{ $t("dish_settings.service_duration") }}
                  </div>
                  <BaseNumberInput
                    placeholder="0"
                    v-model.number="item.service_duration"
                    @blur="save"
                    size="sm"
                    :unit="$t('units.mins')"
                  />
                </div>
                <div
                  class="gap-row-txs w-100"
                  v-else
                >
                  <div class="qrw-label-sm qrw-text-content-base-tertiary bold">
                    {{ $t("menu_settings.weight") }}
                  </div>
                  <BaseNumberInput
                    placeholder="0"
                    v-model.number="item.weight"
                    @blur="save"
                    size="sm"
                    :unit="$t('units.' + dish.unit)"
                  />
                </div>
                <div class="gap-row-txs w-100">
                  <div class="qrw-label-sm qrw-text-content-base-tertiary bold">
                    {{ $t("menu_settings.price") }}
                  </div>
                  <BaseNumberInput
                    placeholder="0"
                    :invalid="item.price == null"
                    v-model.number="item.price"
                    :disabled="isPriceEditingDisabledForItem(item)"
                    @blur="save"
                    size="sm"
                    :unit="currency.symbol"
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="modificator-pos-settings gap-column-sm">
            <div
              class="gap-row-txs w-100"
              v-if="
                canShowPosIntegrationElements &&
                  (canSelectPosterProduct(item) || canSelectIikoProduct(item))
              "
            >
              <div class="qrw-label-sm qrw-text-content-base-tertiary">
                {{ $t("modificators_settings.integration") }}
              </div>
              <PosterMenuInput
                :value="item.poster_product_id"
                @input="(v) => setPriceGroupPosterProductId(item_index, v)"
                @setData="(v) => setPriceGroupPosterData(item_index, v)"
                :hide_with_modificators="false"
                :show_logo="true"
                v-if="canSelectPosterProduct(item)"
              />
              <IikoMenuInput
                :value="item.iiko_product_id"
                :show_logo="true"
                @input="(v) => setPriceGroupIikoProductId(item_index, v)"
                @setData="(v) => setPriceGroupIikoData(item_index, v)"
                v-if="canSelectIikoProduct(item)"
              />
            </div>

            <div class="gap-row-txs">
              <div class="modificators-dependencies-title">
                <div class="qrw-label-sm qrw-text-content-base-tertiary">
                  {{ $t("modificators_settings.dependencies_title") }}
                </div>

                <div
                  class="iqos-tooltip-helper-box"
                  @mouseenter="
                    hoveredModificatorDependencyTipIndex = item_index
                  "
                  @mouseleave="hoveredModificatorDependencyTipIndex = null"
                >
                  <BaseIcon
                    class="qrw-text-content-base-tertiary"
                    icon="info-circle"
                    size="1xs"
                  />
                  <IQOSTooltip
                    :backdrop="false"
                    earPosition="bottom-right"
                    :title="$t('modificators_settings.dependencies_title')"
                    v-if="hoveredModificatorDependencyTipIndex == item_index"
                  >
                    {{ $t("modificators_settings.dependency_self_item") }}
                  </IQOSTooltip>
                </div>
              </div>

              <BasePushButton
                variant="outlined"
                size="sm"
                borderColor="border-base-secondary"
                textColor="content-base-primary"
                class="item-deps-button"
                @click="itemLinkingIndex = item_index"
              >
                {{
                  getModificatorItemDependentsCount(item_index)
                    ? $t("modificators_settings.manage")
                    : $t("modificators_settings.setup")
                }}
                <span
                  class="qrw-text-content-base-tertiary"
                  v-if="getModificatorItemDependentsCount(item_index)"
                >
                  ({{
                    getModificatorItemDependentsCount(item_index)
                  }})
                </span>
              </BasePushButton>
            </div>
          </div>
        </div>

        <BaseDivider
          color="border-base-tertiary"
          vertical
          class="modificator-card-vertical-divider"
          style="height: 100%;"
        />
        <div class="modificator-card-toggles">
          <div class="gap-row-1xs visible-toggle-container">
            <div class="qrw-label-sm qrw-text-content-base-tertiary">
              {{ $t("modificators_settings.visible") }}
            </div>
            <BaseToggleSwitcher
              size="lg"
              :value="item.stock"
              @input="
                (nv) => {
                  item.stock = nv;
                  save();
                }
              "
            />
          </div>
          <BasePushButton
            variant="outlined"
            size="sm"
            @click="itemDeletionIndex = item_index"
            textColor="content-error-primary"
            borderColor="border-error-secondary"
            borderHoverColor="border-error-primary"
            icon="trash"
            :disabled="isPriceGroupRequired"
          >
            {{ $t("menu_settings.delete") }}
          </BasePushButton>
        </div>
      </div>
      <div class="add-modificator-container">
        <BasePushButton
          variant="outlined"
          borderColor="border-base-secondary"
          size="sm"
          textColor="content-base-primary"
          @click="addModificatorItem"
          icon="plus"
        >
          {{ $t("modificators_settings.add_modificator") }}
        </BasePushButton>
      </div>
    </div>
    <div class="modificators-actions">
      <div class="gap-column-xs">
        <BasePushButton
          variant="outlined"
          textColor="content-base-primary"
          borderColor="border-base-secondary"
          size="sm"
          :disabled="price_group_index === 0"
          @click="moveGroup(-1)"
          icon="arrow-up"
        >
          {{ $t("modificators_settings.move_up") }}
        </BasePushButton>
        <BasePushButton
          size="sm"
          variant="outlined"
          textColor="content-base-primary"
          borderColor="border-base-secondary"
          icon="arrow-down"
          @click="moveGroup(1)"
          :disabled="price_group_index === dish.price_groups.length - 1"
        >
          {{ $t("modificators_settings.move_down") }}
        </BasePushButton>
      </div>

      <BasePushButton
        variant="outlined"
        size="sm"
        textColor="content-error-primary"
        borderColor="border-error-secondary"
        borderHoverColor="border-error-primary"
        icon="trash"
        @click="isConfirmingGroupDeletion = true"
        :disabled="isPriceGroupRequired"
      >
        {{ $t("menu_settings.delete") }}
      </BasePushButton>
    </div>

    <DangerConfirmModal
      @confirm="deleteModificatorGroup"
      @cancel="isConfirmingGroupDeletion = false"
      v-if="isConfirmingGroupDeletion"
    >
      {{ $t("modificators_settings.confirm_remove") }}
    </DangerConfirmModal>

    <DangerConfirmModal
      @confirm="deleteModificator()"
      @cancel="itemDeletionIndex = -1"
      v-if="itemDeletionIndex >= 0"
    >
      {{ $t("modificators_settings.confirm_remove_item") }}
    </DangerConfirmModal>

    <ModificatorDependenciesModal
      :priceGroup="price_group"
      @close="managingDependencies = false"
      v-if="managingDependencies"
    />

    <ModificatorDependentsModal
      :itemId="price_group.items[itemLinkingIndex]._id"
      @close="itemLinkingIndex = -1"
      v-if="itemLinkingIndex >= 0"
    />
  </WhiteIQOS>
</template>

<script>
import PosterMenuInput from '@/components/integrations/PosterMenuInput.vue'
import IikoMenuInput from '@/components/integrations/IikoMenuInput.vue'
import ModificatorDependenciesModal from '@/components/menu/ModificatorDependenciesModal.vue'
import ModificatorDependentsModal from '@/components/menu/ModificatorDependentsModal.vue'

export default {
  props: {
    price_group: {
      type: Object,
      required: true
    },
    price_group_index: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      isConfirmingGroupDeletion: false,
      itemDeletionIndex: -1,

      isPosLockTooltipShown: false,
      isDependencyTooltipShown: false,
      isShowingInfoDependenciesTip: false,

      hoveredModificatorDependencyTipIndex: null,

      itemLinkingIndex: -1,

      managingDependencies: false
    }
  },
  components: {
    PosterMenuInput,
    IikoMenuInput,
    ModificatorDependenciesModal,
    ModificatorDependentsModal
  },
  methods: {
    addModificatorItem() {
      this.$store.commit('menu/addPriceGroupItem', {
        index: this.price_group_index
      })

      this.save()
    },
    save() {
      this.$store.dispatch('menu/updateProduct')
    },
    setPriceGroupPosterProductId(item_index, value) {
      this.$set(this.price_group.items[item_index], 'poster_modification_id', null)

      this.$set(this.price_group.items[item_index], 'poster_modificator_id', null)

      this.$set(this.price_group.items[item_index], 'poster_product_id', value)

      if (this.price_group.single_select)
        this.$store.commit('menu/setProductData', {
          key: 'base_price',
          value: 0
        })

      this.emitUpdate()
    },
    setPriceGroupPosterData(item_index, data) {
      this.$set(this.price_group.items[item_index], 'price', data.base_price)
    },
    setPriceGroupIikoProductId(item_index, value) {
      this.$set(this.price_group.items[item_index], 'iiko_product_id', value)

      this.emitUpdate()
    },
    setPriceGroupIikoData(item_index, data) {
      this.$set(this.price_group.items[item_index], 'price', data.price)

      if (data.iiko_group_id) {
        this.$set(this.price_group.items[item_index], 'iiko_group_id', data.iiko_group_id)
      }
    },
    canSelectPosterProduct(item) {
      if (!this.hasPosterIntegration) return false

      if (!this.dish.poster_product_id) return true

      if (this.price_group.single_select) {
        if (this.price_group.poster_group_id) return false
      }

      if (item.poster_modificator_id || item.poster_modification_id) return false

      return true
    },
    canSelectIikoProduct(item) {
      if (!this.hasIikoIntegration) return false

      if (item.iiko_group_id) return false

      if (!this.dish.iiko_product_id) return true

      return true
    },
    isPriceEditingDisabledForItem(item) {
      if (item.poster_product_id) return true

      if (this.isPriceEditingBlocked && (item.poster_modificator_id || item.poster_modification_id)) {
        return true
      }

      if (item.iiko_product_id) return true

      return false
    },
    moveGroup(delta) {
      if (this.price_group_index == 0 && delta == -1) return
      if (this.price_group_index == this.dish.price_groups.length - 1 && delta == 1) return

      this.$store.commit('menu/rearrangePriceGroup', {
        old_index: this.price_group_index,
        new_index: this.price_group_index + delta
      })
      this.save()
    },
    moveGroupItem(item_index, delta) {
      this.$store.commit('menu/rearrangePriceGroupItem', {
        group_index: this.price_group_index,
        item_index,
        delta
      })
      this.save()
    },
    onSingleSelectChange(enable) {
      if (this.isPriceGroupFromPosSystem) return

      this.$set(this.price_group, 'single_select', enable)

      this.save()
    },
    deleteModificator() {
      const name = this.price_group.items[this.itemDeletionIndex].name

      this.$store.commit('menu/removePriceGroupItem', {
        group_index: this.price_group_index,
        item_index: this.itemDeletionIndex
      })

      if (this.price_group.items.length == 0) {
        this.deleteModificatorGroup()
      }

      this.$store.dispatch('auditlog/write', {
        type: 'delete_menu_product_modificator',
        payload: {
          dish_name: this.dish.name,
          dish_id: this.dish._id,
          modificator_name: name
        }
      })

      this.itemDeletionIndex = -1

      this.save()
    },
    deleteModificatorGroup() {
      this.isConfirmingGroupDeletion = false

      let name = this.price_group.name

      this.$store.commit('menu/removePriceGroup', {
        index: this.price_group_index
      })

      this.save()

      this.$store.dispatch('auditlog/write', {
        type: 'delete_menu_product_modificators',
        payload: {
          dish_id: this.dish._id,
          dish_name: this.dish.name,
          name
        }
      })
    },
    getModificatorItemDependentsCount(index) {
      return this.dish.price_groups.filter(pg => pg.show_for && pg.show_for.includes(this.price_group.items[index]._id))
        .length
    }
  },
  computed: {
    dish() {
      return this.$store.state.menu.product
    },
    currency() {
      return this.$store.state.currency
    },
    isLocalizing() {
      return this.$store.getters['menu/isLocalizing']
    },
    currentProductLocaleIndex() {
      return this.$store.getters['menu/currentProductLocaleIndex']
    },
    currentProductLocale() {
      return this.$store.getters['menu/currentProductLocale']
    },
    isPriceGroupFromPosSystem() {
      if (this.dish.poster_product_id) {
        const product = this.$store.getters['poster/findPosterProduct'](this.dish.poster_product_id)

        if (!product) return false

        return product.price_groups.some(pg => pg.poster_group_id == this.price_group.poster_group_id)
      }

      if (this.dish.iiko_product_id) return !!this.price_group.iiko_group_id

      return false
    },
    hasPosterIntegration() {
      return this.$store.getters.hasPosterIntegration
    },
    hasIikoIntegration() {
      return this.$store.getters.hasIikoIntegration
    },
    canShowPosIntegrationElements() {
      return this.hasPosterIntegration || this.hasIikoIntegration
    },
    isPriceEditingBlocked() {
      if (this.hasPosterIntegration && this.dish.poster_product_id) {
        return true
      }

      if (this.hasIikoIntegration && this.dish.iiko_product_id) {
        return true
      }

      return false
    },
    isPriceGroupRequired() {
      let is_required = false

      if (this.hasPosterIntegration && this.dish.poster_product_id) {
        is_required = this.$store.getters['poster/isGroupRequired'](this.dish.poster_product_id, this.price_group)
      }

      if (this.hasIikoIntegration && this.dish.iiko_product_id) {
        is_required = this.$store.getters['iiko/isGroupRequired'](this.dish.iiko_product_id, this.price_group)
      }

      return is_required
    },
    groupDependenciesList() {
      if (!this.price_group.show_for || !this.price_group.show_for.length) return []

      return this.dish.price_groups
        .reduce((acc, group) => {
          return [
            ...acc,
            ...group.items.map(item => {
              return {
                group_id: group._id,
                item_id: item._id,
                name: item.name,
                group_name: group.name
              }
            })
          ]
        }, [])
        .filter(entry => this.price_group.show_for.includes(entry.item_id))
    },
    isUsingServiceReservations() {
      return this.$store.getters.isUsingServiceReservations
    }
  }
}
</script>

<style>
.price-group-panel-iqos {
  padding: 0 !important;
}
</style>

<style scoped>
.price-group-settings {
  display: flex;
  flex-direction: row;
  column-gap: 24px;
  padding: 24px;
  width: 100%;
  box-sizing: border-box;
  background: var(--qrw-background-base-secondary-10);
}

.name-block {
  width: 100%;
}

.deps-button {
  width: fit-content;
  white-space: nowrap;
}

.item-deps-button {
  width: 172px;
  max-width: 172px;
  min-width: 172px;
  white-space: nowrap;
}

.limit-input {
  width: 128px;
}

.modificators-list {
  background: var(--qrw-background-base-primary-10);
  padding: 24px;
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  box-sizing: border-box;
}

.modificator-card {
  border-radius: 12px;
  padding: 24px;
  column-gap: 24px;
  box-shadow: var(--qrw-shadow-card-light);
  width: 100%;
  display: flex;
  box-sizing: border-box;
}

.modificator-card-info {
  display: flex;
  width: 72px;
  min-width: 72px;
  max-width: 72px;
  flex-grow: 1;
  justify-content: space-between;
  flex-direction: column;
}

.modificator-card-toggles {
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  row-gap: 8px;
  flex-grow: 1;
}

.visible-toggle-container {
  align-items: center;
}

.add-modificator-container {
  display: flex;
  width: 100%;
  box-sizing: border-box;
  align-content: center;
  justify-content: center;
}

.modificators-actions {
  display: flex;
  background: var(--qrw-background-base-secondary-10);
  padding: 24px;
  row-gap: 12px;
  align-items: center;
  box-sizing: border-box;
  justify-content: space-between;
  width: 100%;
}

.modificator-card-vertical-divider {
  display: flex;
  flex-grow: 1;
  height: auto !important;
}

.modificators-dependencies-title {
  display: flex;
  flex-direction: row;
  column-gap: 6px;
  align-items: flex-start;
}

.modificator-pos-settings {
  align-items: center;
}
</style>
